/**
 * 🆕 [2026-06-11 v4] Éditeur d'heures d'ouverture pour fournisseurs / magasins.
 * Composant réutilisable : pour un magasin (dans le sub-form magasin de GestionProduits)
 * ou pour un fournisseur principal (FournisseurForm).
 */
import React from 'react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Clock, Copy } from 'lucide-react';
import { JOURS_SEMAINE, heuresParDefaut } from '../../utils/heuresOuverture';

export function HeuresOuvertureEditor({ heures, onChange, testIdPrefix = 'heures' }) {
  const enabled = !!heures;

  const handleToggle = (checked) => {
    onChange(checked ? heuresParDefaut() : null);
  };

  const setJour = (jourKey, patch) => {
    onChange({
      ...heures,
      [jourKey]: { ...(heures[jourKey] || {}), ...patch },
    });
  };

  const copierLundiVersOuvres = () => {
    if (!heures || !heures.lundi) return;
    const lun = heures.lundi;
    onChange({
      ...heures,
      mardi: { ...lun },
      mercredi: { ...lun },
      jeudi: { ...lun },
      vendredi: { ...lun },
    });
  };

  return (
    <div className="space-y-3 rounded-md border border-slate-200 bg-slate-50/60 p-3" data-testid={`${testIdPrefix}-editor`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-slate-600" />
          <Label className="text-sm font-medium text-slate-800">Heures d'ouverture (optionnel)</Label>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">{enabled ? 'Renseignées' : 'Non renseignées'}</span>
          <Switch
            checked={enabled}
            onCheckedChange={handleToggle}
            data-testid={`${testIdPrefix}-toggle`}
          />
        </div>
      </div>

      {enabled && (
        <>
          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-600">
              Le chef d'équipe verra ces heures lors du passage de commande.
            </p>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={copierLundiVersOuvres}
              className="h-7 text-xs"
              data-testid={`${testIdPrefix}-copy-weekdays`}
            >
              <Copy className="mr-1 h-3 w-3" />
              Copier Lun → Mar-Ven
            </Button>
          </div>

          <div className="space-y-1.5">
            {JOURS_SEMAINE.map(j => {
              const d = heures[j.key] || { ouvert: false };
              return (
                <div
                  key={j.key}
                  className="grid grid-cols-12 items-center gap-2 rounded bg-white px-2 py-1.5"
                  data-testid={`${testIdPrefix}-row-${j.key}`}
                >
                  <div className="col-span-3 flex items-center gap-2">
                    <Switch
                      checked={!!d.ouvert}
                      onCheckedChange={(v) => setJour(j.key, {
                        ouvert: v,
                        ouverture: v ? (d.ouverture || '07:00') : null,
                        fermeture: v ? (d.fermeture || '17:00') : null,
                      })}
                      data-testid={`${testIdPrefix}-${j.key}-ouvert`}
                    />
                    <span className="text-sm font-medium text-slate-700">{j.label}</span>
                  </div>
                  {d.ouvert ? (
                    <>
                      <div className="col-span-4 flex items-center gap-1">
                        <span className="text-xs text-slate-500">Ouverture</span>
                        <Input
                          type="time"
                          value={d.ouverture || ''}
                          onChange={(e) => setJour(j.key, { ouverture: e.target.value || null })}
                          className="h-7 text-sm"
                          data-testid={`${testIdPrefix}-${j.key}-ouverture`}
                        />
                      </div>
                      <div className="col-span-4 flex items-center gap-1">
                        <span className="text-xs text-slate-500">Fermeture</span>
                        <Input
                          type="time"
                          value={d.fermeture || ''}
                          onChange={(e) => setJour(j.key, { fermeture: e.target.value || null })}
                          className="h-7 text-sm"
                          data-testid={`${testIdPrefix}-${j.key}-fermeture`}
                        />
                      </div>
                      <div className="col-span-1" />
                    </>
                  ) : (
                    <div className="col-span-9 text-xs italic text-slate-500">Fermé</div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export default HeuresOuvertureEditor;
